import { getAutoLoadConversationKey } from '../../common/constants/private';

/**
 * Unified state stored in localStorage for auto-loading the most recently active
 * conversation when the chat is mounted. Both fields are required so the
 * conversation reference and its activity timestamp cannot drift out of sync —
 * they must only be written together via {@link setAutoLoadConversation}.
 */
export type AutoLoadConversationState = {
	conversationId: string;
	lastActivityTime: number;
};

const isValidState = (value: unknown): value is AutoLoadConversationState => {
	if (typeof value !== 'object' || value === null) {
		return false;
	}
	const candidate = value as Record<string, unknown>;
	return (
		typeof candidate.conversationId === 'string' &&
		typeof candidate.lastActivityTime === 'number' &&
		Number.isFinite(candidate.lastActivityTime)
	);
};

// Helper function to safely access localStorage in case of SSR
const getLocalStorage = (): Storage | undefined => {
	if (typeof window === 'undefined') {
		return undefined;
	}
	return window?.localStorage;
};

/**
 * Reads the unified auto-load conversation state for the given siteId from
 * localStorage. Returns `null` if the entry is missing, not valid JSON, or
 * does not match the expected shape.
 */
export const getAutoLoadConversation = (siteId: string): AutoLoadConversationState | null => {
	const raw = getLocalStorage()?.getItem(getAutoLoadConversationKey(siteId));

	if (!raw) {
		return null;
	}

	try {
		const parsed: unknown = JSON.parse(raw);
		if (!isValidState(parsed)) {
			return null;
		}
		return parsed;
	} catch {
		return null;
	}
};

/**
 * Writes the unified auto-load conversation state for the given siteId.
 * Pass `null` to clear the entry. The single setter enforces that the
 * conversationId and lastActivityTime are always updated together.
 */
export const setAutoLoadConversation = (
	siteId: string,
	state: AutoLoadConversationState | null,
): void => {
	const key = getAutoLoadConversationKey(siteId);

	if (state === null) {
		getLocalStorage()?.removeItem(key);
		return;
	}

	getLocalStorage()?.setItem(key, JSON.stringify(state));
};
